import React, { Suspense, lazy, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import DoctorShell from "@/components/doctor/DoctorShell";
import DoctorProfileEditModal from "@/components/doctor/DoctorProfileEditModal";
import BodyMetricsEditModal from "@/components/dashboard/BodyMetricsEditModal";
import GdprSelfService from "@/components/GdprSelfService";
import PrivacyDashboard from "@/components/privacy/PrivacyDashboard";
import HiddenCardsPanel from "@/components/HiddenCardsPanel";
import MigrateMyLabsCard from "@/components/lab_migration/MigrateMyLabsCard";  // iter754
import LabCoverageHeatMap from "@/components/lab_migration/LabCoverageHeatMap";  // iter755
import HealthConditionsCard from "@/components/health_conditions/HealthConditionsCard";  // iter759
import SEO from "@/components/SEO";
import {
  Loader2, Pencil, Stethoscope, IdCard, Building2, Phone, Mail,
  ShieldCheck, Sparkles, Users, FileText, Brain, CreditCard,
  Sun, Moon, Languages, ArrowLeft, Ruler, Activity,
} from "lucide-react";

const SecuritySettings = lazy(() => import("@/components/SecuritySettings"));

const DOCTOR_ROLES = ["admin", "clinician"];

/**
 * DoctorProfilePage — самостоятелен профил за лекари (iter314).
 * Заменя пациентския /dashboard/profile при кликване на Настройки от DoctorShell.
 *
 * Секции:
 *   1. Професионален профил (име, специалност, лиценз, клиника, контакти)
 *   2. Активност за последните 30 дни (пациенти / SOAP / AI решения)
 *   3. Абонамент Clinic + кредити
 *   4. Сигурност (парола / 2FA)
 *   5. Предпочитания (тема, език)
 *   6. GDPR самообслужване
 */
export default function DoctorProfilePage() {
  const { user, API, refreshUser } = useAuth();
  const { language, setLanguage } = useLanguage();
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const bg = language === "bg";

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [bodyEditOpen, setBodyEditOpen] = useState(false);

  // Access guard — само clinician/admin
  useEffect(() => {
    if (!user) return;
    if (!DOCTOR_ROLES.includes(user.role)) {
      navigate("/dashboard/profile", { replace: true });
    }
  }, [user, navigate]);

  const fetchProfile = useCallback(async () => {
    setLoading(true);
    try {
      const token =
        localStorage.getItem("nutramilo_access_token") ||
        sessionStorage.getItem("nutramilo_access_token");
      const { data } = await axios.get(`${API}/doctor/profile`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setData(data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [API]);

  useEffect(() => { fetchProfile(); }, [fetchProfile]);

  const cardCls = `rounded-2xl border p-4 sm:p-5 ${
    isDark ? "bg-[hsl(222,47%,10%)] border-white/5" : "bg-white border-gray-200"
  }`;
  const labelCls = `text-[10px] uppercase tracking-wider font-semibold ${
    isDark ? "text-gray-500" : "text-gray-500"
  }`;
  const valueCls = `text-sm font-medium mt-0.5 ${
    isDark ? "text-white" : "text-[#1A1A2E]"
  }`;

  const profile = data?.profile || {};
  const stats = data?.stats_30d || {};
  const sub = data?.subscription || {};
  const isAdmin = user?.role === "admin";
  const subLabel = isAdmin ? "Admin" : sub.plan === "clinician" || sub.plan === "clinic"
    ? (bg ? "Clinic план" : "Clinic plan")
    : (bg ? sub.status === "trialing" ? "Пробен" : "Безплатен" : "Free");

  const initials = (data?.name || user?.email || "D").slice(0, 2).toUpperCase();

  const PlaceholderRow = ({ icon: Icon, label, value, fallback }) => (
    <div className="flex items-start gap-3">
      <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0
        ${isDark ? "bg-violet-500/15 text-violet-300" : "bg-violet-50 text-violet-700"}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="min-w-0 flex-1">
        <div className={labelCls}>{label}</div>
        <div className={`${valueCls} truncate`}>
          {value || (
            <span className={isDark ? "text-gray-500 font-normal" : "text-gray-400 font-normal"}>
              {fallback || (bg ? "Не е попълнено" : "Not set")}
            </span>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <>
      <SEO
        title={bg ? "Профил на лекар — Nutramilo" : "Doctor Profile — Nutramilo"}
        description={bg
          ? "Професионални данни, клиника, активност и настройки за сигурност."
          : "Professional data, clinic, activity and security settings."}
        path="/doctor/profile"
      />
      <DoctorShell>
        <div className={`flex-1 overflow-y-auto ${isDark ? "bg-[hsl(222,47%,6%)]" : "bg-[#F8FAFC]"}`}
          data-testid="doctor-profile-page">
          {/* Top header */}
          <header className={`flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b
            ${isDark ? "border-white/5 bg-[hsl(222,47%,8%)]" : "border-gray-200 bg-white"}`}>
            <div className="flex items-center gap-2 min-w-0">
              <button
                type="button"
                onClick={() => navigate("/doctor")}
                className={`lg:hidden p-1.5 rounded-lg flex-shrink-0 ${
                  isDark ? "hover:bg-white/5 text-gray-300" : "hover:bg-gray-100 text-gray-600"
                }`}
                aria-label={bg ? "Назад" : "Back"}
                data-testid="doctor-profile-back"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="min-w-0">
                <h1 className={`text-lg sm:text-2xl font-bold truncate ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
                  {bg ? "Профил и настройки" : "Profile & Settings"}
                </h1>
                <p className={`text-[11px] sm:text-sm mt-0.5 truncate ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                  {bg ? "Лични данни, клиника, активност и сигурност."
                    : "Personal data, clinic, activity and security."}
                </p>
              </div>
            </div>
            <button
              onClick={() => setEditOpen(true)}
              className="inline-flex items-center gap-1.5 px-3 sm:px-4 h-9 sm:h-10 rounded-xl
                bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white text-xs sm:text-sm
                font-semibold hover:opacity-95 transition-opacity shadow-sm shadow-violet-900/20
                flex-shrink-0"
              data-testid="doctor-profile-edit-btn"
            >
              <Pencil className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              {bg ? "Редактирай" : "Edit"}
            </button>
          </header>

          <main className="max-w-5xl mx-auto px-4 sm:px-6 py-4 sm:py-6 space-y-4 sm:space-y-5 pb-24 lg:pb-8">
            {/* iter933 — Reverse switcher: doctors/admins are ALSO patients
                and may want to review their own health record. */}
            <div
              className={`p-3 rounded-xl border flex items-center justify-between gap-3 flex-wrap ${
                isDark ? "border-emerald-400/25 bg-emerald-500/10 text-emerald-100"
                       : "border-emerald-200 bg-emerald-50 text-emerald-900"
              }`}
              data-testid="doctor-role-switcher"
            >
              <div className="text-xs">
                🩺 Клиничен workspace — управлявате пациенти. Можете да отворите и Вашия личен профил като пациент.
              </div>
              <button
                type="button"
                onClick={() => navigate("/dashboard/profile")}
                className={`text-xs font-bold px-3 py-1.5 rounded-lg whitespace-nowrap ${
                  isDark ? "bg-emerald-500/30 hover:bg-emerald-500/40 text-emerald-100"
                         : "bg-emerald-600 hover:bg-emerald-700 text-white"
                }`}
                data-testid="switch-to-personal-profile-btn"
              >
                👤 Отиди в личен профил →
              </button>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-6 h-6 animate-spin text-violet-500" />
              </div>
            ) : (
              <>
                {/* Hero — лекарска визитка */}
                <section className={cardCls} data-testid="doctor-profile-hero">
                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl flex items-center justify-center
                      bg-gradient-to-br from-violet-500 to-fuchsia-600 text-white text-lg sm:text-xl font-bold
                      flex-shrink-0 shadow-lg shadow-violet-900/30">
                      {initials}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h2 className={`text-lg sm:text-xl font-bold ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
                          {data?.name || user?.email}
                        </h2>
                        {data?.clinician_status === "approved" && (
                          <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full
                            ${isDark ? "bg-emerald-500/15 text-emerald-300" : "bg-emerald-50 text-emerald-700"}`}>
                            <ShieldCheck className="w-3 h-3" />
                            {bg ? "Верифициран" : "Verified"}
                          </span>
                        )}
                      </div>
                      <p className={`text-sm mt-0.5 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                        {profile.specialty || (bg ? "Специалност не е посочена" : "No specialty set")}
                      </p>
                      {profile.clinic_name && (
                        <p className={`text-xs mt-1 ${isDark ? "text-gray-500" : "text-gray-500"}`}>
                          {profile.clinic_name}
                          {profile.clinic_address ? ` · ${profile.clinic_address}` : ""}
                        </p>
                      )}
                    </div>
                  </div>
                </section>

                {/* Активност 30 дни */}
                <section className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="doctor-profile-stats">
                  {[
                    { icon: Users, label: bg ? "Пациенти" : "Patients", value: stats.patients_total ?? 0,
                      hint: bg ? `+${stats.patients_new ?? 0} нови` : `+${stats.patients_new ?? 0} new`, color: "violet" },
                    /* iter397 — SOAP бележки stat премахнат по заявка. */
                    { icon: Brain, label: bg ? "AI анализи" : "AI analyses", value: stats.ai_decisions ?? 0,
                      hint: bg ? "посл. 30 дни" : "last 30d", color: "sky" },
                    { icon: CreditCard, label: bg ? "AI анализи" : "AI analyses", value: data?.credits ?? 0,
                      hint: subLabel, color: "emerald" },
                  ].map((s) => {
                    const Icon = s.icon;
                    return (
                      <div key={s.label}
                        className={`${cardCls} p-3 sm:p-4`}
                        data-testid={`doctor-stat-${s.label}`}>
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center mb-2
                          ${isDark ? `bg-${s.color}-500/15 text-${s.color}-300` : `bg-${s.color}-50 text-${s.color}-700`}`}>
                          <Icon className="w-4 h-4" />
                        </div>
                        <div className={`text-xl sm:text-2xl font-bold ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
                          {s.value}
                        </div>
                        <div className={`text-[10px] uppercase tracking-wider mt-0.5 ${isDark ? "text-gray-500" : "text-gray-500"}`}>
                          {s.label}
                        </div>
                        <div className={`text-[10px] mt-0.5 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                          {s.hint}
                        </div>
                      </div>
                    );
                  })}
                </section>

                {/* Личен здравен профил (за тестване / собствени данни) —
                    Bulgarian UX request Feb 2026: администраторът / лекарят
                    трябва да може да редактира собствените си телесни данни,
                    защото те влияят на DIL, NIS, BHI и всички здравни индекси.
                    Тази секция показва текущите стойности и отваря BodyMetricsEditModal. */}
                {(() => {
                  const p = user?.profile || {};
                  const bd = p.body || {};
                  const ms = p.measurements || {};
                  const rows = [
                    { icon: Activity, label: bg ? "Височина" : "Height", value: bd.height, unit: "cm" },
                    { icon: Activity, label: bg ? "Тегло"    : "Weight", value: bd.weight, unit: "kg" },
                    { icon: Ruler,    label: bg ? "Шия"      : "Neck",   value: ms.neck,   unit: "cm" },
                    { icon: Ruler,    label: bg ? "Талия"    : "Waist",  value: ms.waist,  unit: "cm" },
                    { icon: Ruler,    label: bg ? "Ханш"     : "Hip",    value: ms.hip,    unit: "cm" },
                  ];
                  return (
                    <section className={cardCls} data-testid="doctor-profile-body-metrics">
                      <div className="flex items-start justify-between gap-3 mb-3">
                        <div className="min-w-0">
                          <div className={labelCls}>
                            {bg ? "Личен здравен профил" : "Personal health profile"}
                          </div>
                          <h3 className={`text-base font-bold ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
                            {bg ? "Телесни измервания" : "Body measurements"}
                          </h3>
                          <p className={`text-[11px] mt-1 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                            {bg
                              ? "Тези стойности захранват DIL бюджета, NIS оценките и всички здравни индекси. Ако тестваш системата с админ / докторски акаунт, обновявай ги за реалистична обратна връзка."
                              : "These values power DIL budget, NIS scores and all clinical indices. Keep them up to date for realistic feedback while testing with an admin / clinician account."}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => setBodyEditOpen(true)}
                          data-testid="doctor-body-metrics-edit"
                          className="inline-flex items-center gap-1.5 px-3 h-9 rounded-xl
                            bg-gradient-to-r from-emerald-500 to-teal-600 text-white text-xs sm:text-sm
                            font-semibold hover:opacity-95 transition-opacity shadow-sm shadow-emerald-900/20
                            flex-shrink-0"
                        >
                          <Ruler className="w-3.5 h-3.5" />
                          {bg ? "Редактирай" : "Edit"}
                        </button>
                      </div>
                      {/* Feb 2026 — Rerun onboarding CTA. Lets admin / clinician
                          re-drive the entire wizard for testing or to update
                          long-term goals (weight_loss → maintenance, etc.). */}
                      <div className="flex items-center justify-between gap-3 -mt-1 mb-3">
                        <p className={`text-[11px] ${isDark ? "text-gray-500" : "text-gray-500"}`}>
                          {bg
                            ? "Ако искаш да минеш през целия wizard отново (напр. смяна на цел), използвай „Пусни отново onboarding\"."
                            : "To re-drive the full onboarding wizard (e.g. change your primary goal), use \"Rerun onboarding\"."}
                        </p>
                        <button
                          type="button"
                          onClick={() => navigate("/onboarding?force=1")}
                          data-testid="doctor-rerun-onboarding"
                          className={`inline-flex items-center gap-1.5 px-3 h-8 rounded-full text-[11px] font-semibold flex-shrink-0 transition-colors ${
                            isDark ? "bg-violet-500/15 text-violet-200 hover:bg-violet-500/25" : "bg-violet-100 text-violet-900 hover:bg-violet-200"
                          }`}
                        >
                          <Sparkles className="w-3 h-3" />
                          {bg ? "Пусни отново onboarding" : "Rerun onboarding"}
                        </button>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                        {rows.map((r) => {
                          const Icon = r.icon;
                          const hasValue = r.value != null && r.value !== "";
                          return (
                            <div
                              key={r.label}
                              className={`rounded-xl p-3 border ${
                                hasValue
                                  ? (isDark ? "bg-emerald-500/5 border-emerald-900/40" : "bg-emerald-50/60 border-emerald-200")
                                  : (isDark ? "bg-white/[0.02] border-white/10" : "bg-gray-50 border-gray-200")
                              }`}
                              data-testid={`doctor-body-metric-${r.label}`}
                            >
                              <div className="flex items-center gap-1.5 mb-1">
                                <Icon className={`w-3 h-3 ${isDark ? "text-emerald-300" : "text-emerald-700"}`} />
                                <div className={`text-[10px] uppercase tracking-wider font-semibold ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                                  {r.label}
                                </div>
                              </div>
                              <div className={`text-lg font-bold tabular-nums ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
                                {hasValue ? (
                                  <>
                                    {r.value}
                                    <span className={`text-[10px] font-normal ml-1 ${isDark ? "text-gray-400" : "text-gray-500"}`}>{r.unit}</span>
                                  </>
                                ) : (
                                  <span className={`text-sm font-normal ${isDark ? "text-gray-500" : "text-gray-400"}`}>—</span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </section>
                  );
                })()}

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-5">
                  {/* Професионални данни */}
                  <section className={cardCls} data-testid="doctor-profile-professional">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <div className={labelCls}>{bg ? "Професионални данни" : "Professional details"}</div>
                        <h3 className={`text-base font-bold ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
                          {bg ? "Идентификация" : "Identification"}
                        </h3>
                      </div>
                      <Sparkles className={`w-4 h-4 ${isDark ? "text-violet-300" : "text-violet-500"}`} />
                    </div>
                    <div className="space-y-3">
                      <PlaceholderRow icon={Stethoscope} label={bg ? "Специалност" : "Specialty"} value={profile.specialty} />
                      <PlaceholderRow icon={IdCard} label={bg ? "УИН / лиценз" : "License / UIN"} value={profile.license_number} />
                      <PlaceholderRow icon={Building2} label={bg ? "Клиника" : "Clinic"} value={profile.clinic_name} />
                      <PlaceholderRow icon={Building2} label={bg ? "Адрес" : "Address"} value={profile.clinic_address} />
                    </div>
                  </section>

                  {/* Контакти */}
                  <section className={cardCls} data-testid="doctor-profile-contacts">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <div className={labelCls}>{bg ? "Контакти" : "Contacts"}</div>
                        <h3 className={`text-base font-bold ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
                          {bg ? "Връзка с вас" : "Reach out"}
                        </h3>
                      </div>
                    </div>
                    <div className="space-y-3">
                      <PlaceholderRow icon={Mail} label="Email" value={data?.email} />
                      <PlaceholderRow icon={Phone} label={bg ? "Телефон" : "Phone"} value={profile.phone} />
                      <PlaceholderRow icon={Building2} label={bg ? "Институция" : "Institution"} value={profile.institution} />
                      <PlaceholderRow icon={IdCard} label="ORCID" value={profile.orcid} />
                    </div>
                  </section>
                </div>

                {/* Bio */}
                {profile.bio && (
                  <section className={cardCls} data-testid="doctor-profile-bio">
                    <div className={labelCls}>{bg ? "Кратка биография" : "Short bio"}</div>
                    <p className={`mt-2 text-sm leading-relaxed break-words whitespace-pre-wrap line-clamp-6 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                      {profile.bio}
                    </p>
                  </section>
                )}

                {/* Предпочитания */}
                <section className={cardCls} data-testid="doctor-profile-preferences">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className={`text-base font-bold ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
                      {bg ? "Предпочитания" : "Preferences"}
                    </h3>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={toggleTheme}
                      className={`flex items-center justify-between gap-3 p-3 rounded-xl border transition-colors
                        ${isDark ? "border-white/10 hover:bg-white/5" : "border-gray-200 hover:bg-gray-50"}`}
                      data-testid="doctor-pref-theme"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-9 h-9 rounded-xl flex items-center justify-center
                          ${isDark ? "bg-amber-500/15 text-amber-300" : "bg-amber-50 text-amber-700"}`}>
                          {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                        </div>
                        <div className="text-left">
                          <div className={`text-sm font-semibold ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
                            {bg ? "Тема" : "Theme"}
                          </div>
                          <div className={`text-[11px] ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                            {isDark ? (bg ? "Тъмна" : "Dark") : (bg ? "Светла" : "Light")}
                          </div>
                        </div>
                      </div>
                      <span className={`text-[10px] font-semibold ${isDark ? "text-violet-300" : "text-violet-700"}`}>
                        {bg ? "Превключи" : "Toggle"}
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setLanguage(bg ? "en" : "bg")}
                      className={`flex items-center justify-between gap-3 p-3 rounded-xl border transition-colors
                        ${isDark ? "border-white/10 hover:bg-white/5" : "border-gray-200 hover:bg-gray-50"}`}
                      data-testid="doctor-pref-language"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-9 h-9 rounded-xl flex items-center justify-center
                          ${isDark ? "bg-sky-500/15 text-sky-300" : "bg-sky-50 text-sky-700"}`}>
                          <Languages className="w-4 h-4" />
                        </div>
                        <div className="text-left">
                          <div className={`text-sm font-semibold ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
                            {bg ? "Език" : "Language"}
                          </div>
                          <div className={`text-[11px] ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                            {bg ? "Български" : "English"}
                          </div>
                        </div>
                      </div>
                      <span className={`text-[10px] font-semibold ${isDark ? "text-violet-300" : "text-violet-700"}`}>
                        {bg ? "EN" : "BG"}
                      </span>
                    </button>
                  </div>
                </section>

                {/* Сигурност + GDPR */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-5">
                  <Suspense fallback={
                    <div className={`${cardCls} flex items-center justify-center py-8`}>
                      <Loader2 className="w-5 h-5 animate-spin" />
                    </div>
                  }>
                    <SecuritySettings />
                  </Suspense>
                  <div className="space-y-4">
                    {/* iter327Z — Възстановяване на скрити карти (показва се при ≥3 скрити модула) */}
                    <HiddenCardsPanel />
                    {/* iter754 — Migrate My Labs (1-клик обединяване на всичките lab колекции) */}
                    <MigrateMyLabsCard />
                    {/* iter755 — Lab Coverage Heat-Map (визуална карта на 44+ маркера) */}
                    <LabCoverageHeatMap />
                    {/* iter759 — Structured Health Conditions */}
                    <HealthConditionsCard />
                    <div className={cardCls} data-testid="doctor-profile-gdpr">
                      <GdprSelfService />
                    </div>
                    {/* iter367 Phase-3 — Privacy Dashboard */}
                    <div className={cardCls} data-testid="doctor-profile-privacy">
                      <PrivacyDashboard isDark={isDark} />
                    </div>
                  </div>
                </div>
              </>
            )}
          </main>
        </div>
      </DoctorShell>

      <DoctorProfileEditModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        initial={data}
        onSaved={() => {
          setEditOpen(false);
          fetchProfile();
          refreshUser?.();
        }}
      />

      {/* Body Metrics editor — visible for doctor/admin so they can update
          their own body data (feeds DIL, NIS, BHI). Feb 2026 UX fix. */}
      <BodyMetricsEditModal
        open={bodyEditOpen}
        onClose={() => setBodyEditOpen(false)}
        API={API}
        user={user}
        isDark={isDark}
        bg={bg}
        onSaved={async () => { await refreshUser?.(); fetchProfile(); }}
      />
    </>
  );
}
